// ============================================================
// TMR Portal Admin — Modelos de contrato (rota /modelos-contrato)
// ============================================================
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Btn } from '../../components/primitives';
import { Notice } from '../../components/Notice';
import { FilterTabs, PageHeader, Table, Td } from '../../components/ui';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Icons } from '../../lib/icons';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import {
  useContractTemplates,
  useImportLegacyTemplates,
  useLegacyImportPreview,
} from '../../data/contractTemplates/queries';
import {
  globalChangedAfterOverride,
  legacySlotHolder,
  sortTemplates,
  templateScopeLabel,
  templateStateSummary,
  templateStatusLabel,
} from '../../data/contractTemplates/status';
import type { ContractTemplateSummary } from '../../data/contractTemplates/contract';
import { LegacyImportNotice } from './LegacyImportNotice';
import { NewTemplateModal } from './NewTemplateModal';

/**
 * The texts patients sign, and the clinics that negotiated their own.
 *
 * **This screen is about coverage before it is about editing.** Its first job is to answer "could a
 * contract of this type be issued right now" — a document type with no published global template looks
 * like nothing at all on a list of templates, and means every sale of that type fails at the moment of
 * issue. The backend computes that gap rather than leaving the screen to infer it from rows it has
 * filtered (`24` §9).
 *
 * The second is to show which clinics are off the global text, and whether the text they departed from has
 * moved on since. An override does not follow the global — that is the point of it, and also its trap.
 *
 * Ported from the v1.1's Portal da Clínica, where it never belonged: who writes, versions and publishes the
 * contract a patient signs is the TMR (`24` §13).
 */
export function ContractTemplatesPage() {
  // Open when a template is being created; carries the document type when the modal was opened from the
  // warning about a type with no global template.
  const [creating, setCreating] = useState<{ documentType?: string } | null>(null);
  // In use first: an archived override is history — kept because contracts issued from it still point at
  // it — and mixed into the list it reads as one more clinic off the global text.
  const [shown, setShown] = useState<'Active' | 'Archived'>('Active');
  const navigate = useNavigate();

  const canRead = usePermission(PERMISSIONS.contractTemplatesRead);
  const canManage = usePermission(PERMISSIONS.contractTemplatesManage);

  const query = useContractTemplates(canRead);
  // What the v1.1 holds that is not here yet. It changes what the screen may offer: a type whose global
  // exists only over there is not a type to "create the global" of (`24` P2b).
  const legacyImport = useLegacyImportPreview(canRead);
  const importLegacy = useImportLegacyTemplates();

  // The route is already gated (Portal.tsx), so this is the operator who reached it by URL. Saying so
  // beats an error state that reads like a fault — the server is right to refuse (06 §6).
  if (!canRead) {
    return (
      <div className="pa-screen" style={{ padding: 24 }}>
        <div className="pa-card">
          <EmptyState
            message="Você não tem permissão para ver os modelos de contrato."
            hint="Peça o acesso a quem administra os papéis."
          />
        </div>
      </div>
    );
  }

  const templates = sortTemplates(query.data?.templates ?? []);
  const gaps = query.data?.typesWithoutGlobalTemplate ?? [];
  const visible = templates.filter((item) => item.status === shown);
  const archivedCount = templates.length - templates.filter((item) => item.status === 'Active').length;

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        eyebrow="Contratos de paciente"
        title="Modelos de contrato"
        desc="O texto que o paciente assina. Publicar uma versão muda o que passa a ser emitido daí em diante — os contratos já emitidos mantêm o texto deles."
        actions={
          canManage && !query.isPending && !query.isError ? (
            <Btn variant="primary" icon={Icons.plus} onClick={() => setCreating({})}>
              Novo modelo
            </Btn>
          ) : undefined
        }
      />

      {query.isPending ? (
        <Skeleton rows={6} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <>
          <LegacyImportNotice preview={legacyImport} importing={importLegacy} canManage={canManage} />

          {gaps.map((gap) =>
            legacySlotHolder(legacyImport.data, gap.code, null) ? (
              // The global exists — over there. Offering to create one here would produce a template the
              // legacy refuses forever; the notice above offers to bring it instead.
              <Notice key={gap.code} tone="warning">
                O modelo global de <strong>{gap.name}</strong> existe no v1.1 e ainda não foi trazido para cá.
                Importe-o no aviso acima.
              </Notice>
            ) : (
              <Notice key={gap.code} tone="warning">
                Nenhum modelo global de <strong>{gap.name}</strong> está publicado. Enquanto isso, um contrato
                desse tipo não é emitido.
                {canManage && (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="pa-link-btn"
                      onClick={() => setCreating({ documentType: gap.code })}
                      style={{ font: 'inherit' }}
                    >
                      Criar o modelo global
                    </button>
                  </>
                )}
              </Notice>
            ),
          )}

          {archivedCount > 0 && (
            <FilterTabs
              value={shown}
              onChange={(value) => setShown(value as 'Active' | 'Archived')}
              tabs={[
                { value: 'Active', label: 'Em uso', count: templates.length - archivedCount },
                { value: 'Archived', label: 'Exceções arquivadas', count: archivedCount },
              ]}
            />
          )}

          {visible.length === 0 && shown === 'Archived' ? (
            <div className="pa-card">
              <EmptyState
                message="Nenhuma exceção arquivada."
                hint="Ao arquivar a exceção de uma clínica, ela aparece aqui e a clínica volta a assinar pelo modelo global."
              />
            </div>
          ) : visible.length === 0 ? (
            <div className="pa-card">
              <EmptyState
                message="Nenhum modelo de contrato cadastrado."
                hint="Um modelo global por tipo de documento é o mínimo para que um contrato seja emitido."
                action={
                  canManage ? (
                    <Btn variant="primary" icon={Icons.plus} onClick={() => setCreating({})}>
                      Novo modelo
                    </Btn>
                  ) : undefined
                }
              />
            </div>
          ) : (
            <Table
              columns={[
                { label: 'Modelo' },
                { label: 'Tipo de documento' },
                { label: 'Alcance' },
                { label: 'Situação' },
                { label: '', width: 110 },
              ]}
            >
              {visible.map((item) => (
                <TemplateRow
                  key={item.id}
                  template={item}
                  staleAgainstGlobal={globalChangedAfterOverride(item, templates)}
                  onOpen={() => navigate(`/modelos-contrato/${item.id}`)}
                />
              ))}
            </Table>
          )}
        </>
      )}

      {creating && (
        <NewTemplateModal
          templates={templates}
          legacyImport={legacyImport.data}
          initialDocumentType={creating.documentType}
          onClose={(templateId) => {
            setCreating(null);
            if (templateId) navigate(`/modelos-contrato/${templateId}`);
          }}
        />
      )}
    </div>
  );
}

/** One template's row. Kept beside the page because nothing else shows a template this way. */
function TemplateRow({
  template,
  staleAgainstGlobal,
  onOpen,
}: {
  template: ContractTemplateSummary;
  staleAgainstGlobal: boolean;
  onOpen: () => void;
}) {
  return (
    <tr>
      <Td>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontWeight: 600 }}>{template.name}</span>
          <span style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
            {templateStateSummary(template)}
          </span>
        </div>
      </Td>
      <Td>{template.documentTypeName}</Td>
      <Td>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span>{templateScopeLabel(template)}</span>
          {staleAgainstGlobal && (
            <span style={{ fontSize: 12.5, color: 'oklch(38% 0.10 60)' }}>
              O modelo global mudou depois desta exceção
            </span>
          )}
        </div>
      </Td>
      <Td>{templateStatusLabel(template.status)}</Td>
      <Td align="right">
        <Btn variant="secondary" onClick={onOpen}>
          Abrir
        </Btn>
      </Td>
    </tr>
  );
}
